import { useEffect, useRef, useState } from "react";
import SlideOutMenuSectioned from "@components/SlideOutMenu/SlideOutMenuSectioned";
import MultiSelectAutocomplete from "@components/Forms/MultiSelectAutocomplete/MultiSelectAutocomplete";
import FormDateField from "@components/Forms/FormDateField";
import { ConfirmDeleteModal } from "@components/CatalogIndex/ConfirmDeleteModal";
import type { ProjectsViewFilters } from "../types";
import "./FilterSlideout.css";
import { useTypeAheadOptions } from "./useFilterOptions";
import { activeFilterCount } from "./filterSummary";
import type { FilterOption, FilterOptionList, FilterOptions, TypeAheadTerms } from "./useFilterOptions";

export interface FilterSlideoutProps {
  show: boolean;
  onClose: () => void;
  filters: ProjectsViewFilters;
  options: FilterOptions;
  onApply: (next: ProjectsViewFilters) => void;
}

type ListKey =
  | "clients"
  | "salesExecs"
  | "presalesEngineers"
  | "teams"
  | "teammates"
  | "tags"
  | "rateTableIds"
  | "lobIds"
  | "businessUnits";
type DateKey = "dateEditedStart" | "dateEditedEnd" | "dateCreatedStart" | "dateCreatedEnd";

const SEARCH_DEBOUNCE_MS = 300;
type SearchKey = keyof TypeAheadTerms;
const EMPTY_TERMS: TypeAheadTerms = { clients: "", salesExecs: "", presalesEngineers: "", tags: "" };
const SEARCH_KEY: Partial<Record<ListKey, SearchKey>> = {
  clients: "clients",
  salesExecs: "salesExecs",
  presalesEngineers: "presalesEngineers",
  tags: "tags",
};

const FilterSlideout = ({ show, onClose, filters, options, onApply }: FilterSlideoutProps) => {
  const [draft, setDraft] = useState<ProjectsViewFilters>(filters);
  // Reset only when the panel opens, not whenever `filters` changes identity.
  const wasShown = useRef(false);
  useEffect(() => {
    if (show && !wasShown.current) setDraft(filters);
    wasShown.current = show;
  }, [show, filters]);

  const [confirmClear, setConfirmClear] = useState(false);

  const [rawTerms, setRawTerms] = useState<TypeAheadTerms>(EMPTY_TERMS);
  const [terms, setTerms] = useState<TypeAheadTerms>(EMPTY_TERMS);
  useEffect(() => {
    const t = setTimeout(() => setTerms(rawTerms), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [rawTerms]);
  const searched = useTypeAheadOptions(terms);
  // Every option seen so far, so selections made from search results stay labelled.
  const known = useRef(new Map<string, FilterOption>());

  // Legacy stores tag ids in `tagIds` and reads them back by id; `tags` holds names for the API.
  const tagIdsFor = (d: ProjectsViewFilters, names: string[]): string[] =>
    names.flatMap((name) => {
      const id =
        known.current.get(`tags:${name}`)?.id ??
        d.tagIds?.[(d.tags ?? []).indexOf(name)];
      return id ? [id] : [];
    });
  const setList = (key: ListKey, values: string[]) =>
    setDraft((d) => ({ ...d, [key]: values, ...(key === "tags" ? { tagIds: tagIdsFor(d, values) } : {}) }));
  const setDate = (key: DateKey, value: string) => setDraft((d) => ({ ...d, [key]: value }));

  const multi = (label: string, key: ListKey, list: FilterOptionList) => {
    const selected = (draft[key] as string[] | undefined) ?? [];
    const searchKey = SEARCH_KEY[key];
    const merged = new Map<string, FilterOption>();
    const found = searchKey ? searched[searchKey].options : [];
    [...list.options, ...found].forEach((o) => {
      merged.set(o.value, o);
      known.current.set(`${key}:${o.value}`, o);
    });
    const options = Array.from(merged.values());
    const value = selected.map(
      (v) => merged.get(v) ?? known.current.get(`${key}:${v}`) ?? { value: v, label: v }
    );
    return (
      <div className="projectsFilterField" key={key}>
        <MultiSelectAutocomplete
          className="slideOutMultiSelect"
          label={label}
          width="500px"
          // untyped JS component infers every destructured prop as required
          onInputChange={(input: string) => {
            if (searchKey) setRawTerms((t) => (t[searchKey] === input ? t : { ...t, [searchKey]: input }));
          }}
          updateRef={undefined}
          options={options}
          value={value}
          onChange={(e: { value: string }[] | null) =>
            setList(key, (e ?? []).map((item) => item.value))
          }
        />
        {(list.failed || (searchKey && searched[searchKey].failed)) && <small className="text-muted">Couldn't load options.</small>}
      </div>
    );
  };

  const date = (label: string, key: DateKey) => (
    <FormDateField
      key={key}
      label={label}
      placeholder={undefined}
      defaultValue={undefined}
      required={undefined}
      updateRef={undefined}
      value={draft[key] ?? ""}
      onChange={(e: { target: { value: string } }) => setDate(key, e.target.value)}
    />
  );

  const dateRange = (startKey: DateKey, endKey: DateKey) => (
    <div className="projectsFilterDateRange">
      <div className="projectsFilterDateField">{date("Start Date", startKey)}</div>
      <span className="projectsFilterDateTo" aria-hidden="true">
        to
      </span>
      <div className="projectsFilterDateField">{date("End Date", endKey)}</div>
    </div>
  );

  const projectInfo = [
    multi("Client Name", "clients", options.clients),
    multi("Sales Executive", "salesExecs", options.salesExecs),
    multi("Pre-Sales Engineer", "presalesEngineers", options.presalesEngineers),
    multi("Teams", "teams", options.teams),
    multi("Teammates", "teammates", options.teammates),
    multi("Project Tags", "tags", options.tags),
    multi("Rate Table", "rateTableIds", options.rateTables),
    multi("Line of Business", "lobIds", options.lobs),
    options.businessUnits.options.length > 0
      ? multi(options.businessUnitLabel, "businessUnits", options.businessUnits)
      : null,
  ];

  const count = activeFilterCount(draft);

  // Clear All keeps "My Projects" and the saved status (which drives the tab).
  const clearAll = () => {
    setConfirmClear(false);
    onApply({
      ...(filters.showMyProjects === undefined ? {} : { showMyProjects: filters.showMyProjects }),
      ...(filters.status === undefined ? {} : { status: filters.status }),
    });
    onClose();
  };

  return (
    <>
    <SlideOutMenuSectioned
      show={show}
      setShow={(v: boolean) => !v && onClose()}
      menuHeader="Filter Results By"
      actionButtonText="Apply"
      onActionButtonClick={() => {
        onApply(draft);
        onClose();
      }}
      clearFields={count > 0 ? `Clear All (${count})` : null}
      onClearFieldsClick={() => setConfirmClear(true)}
      onCancel={onClose}
      sectionsArray={[
        { sectionTitle: "Project Info", sectionClass: "projectInfoSection", sectionContent: projectInfo },
        {
          sectionTitle: "Date Last Edited",
          sectionClass: "dateEditedSection",
          sectionContent: dateRange("dateEditedStart", "dateEditedEnd"),
        },
        {
          sectionTitle: "Date Created",
          sectionClass: "dateCreatedSection",
          sectionContent: dateRange("dateCreatedStart", "dateCreatedEnd"),
        },
      ]}
    />
    <ConfirmDeleteModal
      open={confirmClear}
      title="Clear all filters?"
      message="This removes every filter and refreshes the project list."
      confirmLabel="Clear All"
      onConfirm={clearAll}
      onCancel={() => setConfirmClear(false)}
    />
    </>
  );
};

export default FilterSlideout;
